import { h } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  monogramPreviewLayers,
  monogramTextPreviewLines,
} from "./monogramTypeConfig";
import { monogramFontFamily } from "./monogramFonts";

/**
 * The live monogram preview, rendered one of two ways depending on the type:
 *
 * INITIALS: each letter is a separate SVG that, sharing a common viewBox within
 * a style, stacks directly on top of the others with inter-letter alignment
 * already baked in — so layers are simply overlaid at full size. The
 * single-color letter SVGs are fetched and recolored to the thread color, and
 * swapped in together once all are ready so switching style/type doesn't flash.
 *
 * LINES (Single / Double Line): rendered as live text in the custom font and
 * metrics of the style's matching `text` entry.
 *
 * Rendering as inline DOM (rather than canvas) keeps the door open for
 * snapshotting the preview to an image for the product-detail view.
 */

const DEFAULT_COLOR = "#000000";

// The two surfaces the preview can sit on. The standard background mirrors
// --color-background; the alternate is a one-off backing used when it reads a
// pale thread color better. Keep both in sync with _monogram.scss.
const PREVIEW_BACKGROUND = "#f9f7f7";
const ALT_PREVIEW_BACKGROUND = "#EDEBEB";

function hexToRgb(hex) {
  const value = String(hex).trim().replace(/^#/, "");
  const full =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

// WCAG relative luminance / contrast ratio, trimmed to just what the pale-color
// check needs. Returns null for anything that isn't a parseable hex color.
function relativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((channel) => {
    const s = channel / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(foreground, background) {
  const a = relativeLuminance(foreground);
  const b = relativeLuminance(background);
  if (a === null || b === null) return null;
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/**
 * Whether the alternate backing reads the thread color better than the standard
 * surface — i.e. it gives the higher contrast ratio. Unparseable colors keep the
 * standard surface.
 */
export function prefersAlternateBackground(color) {
  const standard = contrastRatio(color, PREVIEW_BACKGROUND);
  const alternate = contrastRatio(color, ALT_PREVIEW_BACKGROUND);
  if (standard === null || alternate === null) return false;
  return alternate > standard;
}

// Letter SVGs are fetched once per URL and the in-flight/resolved promise is
// cached for the session, so switching color, re-opening the modal, or
// re-entering an initial reuses a letter already loaded.
const svgCache = new Map();

function fetchSvg(url) {
  if (!svgCache.has(url)) {
    const request = fetch(url)
      .then((response) => (response.ok ? response.text() : ""))
      .catch(() => "");
    svgCache.set(url, request);
  }
  return svgCache.get(url);
}

/**
 * Recolor a single-color letter SVG to `color` and size it to fill its layer
 * (contain). Every path — plus any element with an explicit, non-`none` fill —
 * is repainted; `fill="none"` wrappers are left untouched.
 *
 * The fill is set via inline `style` rather than the `fill` attribute: some
 * letters color the glyph through a `<style>` block, and a CSS rule outranks a
 * `fill` presentation attribute — so only an inline style reliably overrides it.
 */
function recolorSvg(rawSvg, color) {
  const fill = color || DEFAULT_COLOR;
  const doc = new DOMParser().parseFromString(rawSvg, "image/svg+xml");
  const svg = doc.querySelector("svg");
  if (!svg) return "";

  svg.setAttribute("width", "100%");
  svg.setAttribute("height", "100%");
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");

  svg.querySelectorAll("*").forEach((el) => {
    const elFill = el.getAttribute("fill");
    const isPath = el.tagName.toLowerCase() === "path";
    if (isPath || (elFill !== null && elFill.toLowerCase() !== "none")) {
      el.style.fill = fill;
    }
  });

  return svg.outerHTML;
}

export default function MonogramPreview({ type, values, style, color }) {
  const layers = useMemo(
    () => monogramPreviewLayers(type, values, style),
    [type, values, style],
  );

  const textLines = useMemo(
    () => monogramTextPreviewLines(type, values, style),
    [type, values, style],
  );

  // The currently displayed letters, each with its recolored markup. Held
  // separately from `layers` so a new selection is only shown once all of its
  // letters have loaded — the previous render stays put until then.
  const [rendered, setRendered] = useState([]);

  useEffect(() => {
    if (!layers.length) {
      setRendered([]);
      return;
    }

    let cancelled = false;
    Promise.all(
      layers.map((layer) =>
        fetchSvg(layer.url).then((rawSvg) => ({
          key: layer.key,
          letter: layer.letter,
          markup: rawSvg ? recolorSvg(rawSvg, color) : "",
        })),
      ),
    ).then((next) => {
      if (!cancelled) setRendered(next.filter((layer) => layer.markup));
    });

    return () => {
      cancelled = true;
    };
  }, [layers, color]);

  if (textLines.length) {
    const textLabel = textLines.map((line) => line.text).join(" ");

    return (
      <div className="monogram-preview" role="img" aria-label={textLabel}>
        <div className="monogram-preview__lines">
          {textLines.map(({ key, text, entry }) => {
            const family = monogramFontFamily(entry.font?.url);
            return (
              <span
                key={key}
                className="monogram-preview__line"
                style={{
                  fontFamily: family ? `"${family}", serif` : undefined,
                  fontSize: `calc(var(--monogram-preview-base) * ${
                    Number(entry.size) || 1
                  })`,
                  lineHeight: Number(entry.lineHeight) || 1,
                  letterSpacing: `${Number(entry.letterSpacing) || 0}em`,
                  color: color || DEFAULT_COLOR,
                }}
              >
                {text}
              </span>
            );
          })}
        </div>
      </div>
    );
  }

  if (!rendered.length) {
    return <div className="monogram-preview" aria-hidden="true" />;
  }

  const label = rendered
    .map((layer) => layer.letter)
    .filter(Boolean)
    .join(" ");

  return (
    <div className="monogram-preview" role="img" aria-label={label}>
      <div className="monogram-preview__stack">
        {rendered.map((layer) => (
          <span
            key={layer.key}
            className="monogram-preview__letter"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: layer.markup }}
          />
        ))}
      </div>
    </div>
  );
}
